import { Injectable } from '@angular/core';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { ACCOUNT_SETTINGS, USER_ROLES } from '@domain/constants/app-variables';
import { UtilService } from '@service/util/util.service';
import { IS_NOT_EMPTY, IS_NOT_NULL_OR_UNDEFINED, RestService } from 'cargo-chief-commons';
import { ProjectProbability } from '@domain/interface/project-probability';
import { environment } from '@environment/environment';
import { TRAILER_TYPES_DB } from '@domain/constants/trailer-types';
import { UrlService } from '../url/url.service';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { LoginSetupService } from '../login-setup/login-setup.service';
import { PICKUP_DATE_TYPES_DB } from '@domain/constants/pickup-date-types';

@Injectable({
	providedIn: 'root',
})
export class AccountSettingsService {
	constructor(
		private cargochief: Cargochief,
		private utilService: UtilService,
		private urlService: UrlService,
		private restService: RestService,
		private loginSetupService: LoginSetupService
	) {}

	get SEARCH_TRUCK_RESULTS_COUNT(): number {
		return this.parseNumericValues(ACCOUNT_SETTINGS.SEARCH_TRUCK_RESULTS_COUNT);
	}

	get SEARCH_TRUCK_ORIGIN_DEADHEAD_LIMIT(): number {
		return this.parseNumericValues(ACCOUNT_SETTINGS.SEARCH_TRUCK_ORIGIN_DEADHEAD_LIMIT);
	}

	get SEARCH_TRUCK_DESTINATION_DEADHEAD_LIMIT(): number {
		return this.parseNumericValues(ACCOUNT_SETTINGS.SEARCH_TRUCK_DESTINATION_DEADHEAD_LIMIT);
	}

	get SEARCH_TRUCK_PICKUP_DATE_RANGE(): number {
		return this.parseNumericValues(ACCOUNT_SETTINGS.SEARCH_TRUCK_PICKUP_DATE_RANGE);
	}

	get SEARCHABLE_TRAILER_TYPES(): Array<any> {
		let trailerTypes: Array<any> = [],
			accountSettings: any = this.cargochief.getAccountSettings();
		if (
			IS_NOT_NULL_OR_UNDEFINED(accountSettings) &&
			IS_NOT_EMPTY(accountSettings[ACCOUNT_SETTINGS.SEARCHABLE_TRAILER_TYPES])
		) {
			accountSettings[ACCOUNT_SETTINGS.SEARCHABLE_TRAILER_TYPES].split(',').map((trailer) => {
				trailerTypes.push({ dbVal: trailer.trim().toUpperCase(), value: trailer });
			});
		} else {
			Object.keys(TRAILER_TYPES_DB).forEach((key) => {
				trailerTypes.push({ dbVal: key, value: TRAILER_TYPES_DB[key] });
			});
		}
		return trailerTypes;
	}

	get DEFAULT_TRAILER_TYPES(): Array<any> {
		let defaultTrailerTypes: Array<any> = [],
			accountSettings: any = this.cargochief.getAccountSettings();
		if (
			IS_NOT_NULL_OR_UNDEFINED(accountSettings) &&
			IS_NOT_EMPTY(accountSettings.DEFAULT_TRAILER_TYPES)
		) {
			accountSettings.DEFAULT_TRAILER_TYPES.split(',').map((trailer) => {
				defaultTrailerTypes.push(trailer.trim());
			});
		}
		return defaultTrailerTypes;
	}

	get PICKUP_DATE_TYPES(): Array<any> {
		let pickupDateTypes: Array<any> = [],
			accountSettings: any = this.cargochief.getAccountSettings();
		if (
			IS_NOT_NULL_OR_UNDEFINED(accountSettings) &&
			IS_NOT_EMPTY(accountSettings.PICK_UP_DATE_TYPES)
		) {
			accountSettings.PICK_UP_DATE_TYPES.split(',').map((pickupDate) => {
				pickupDateTypes.push({ dbVal: pickupDate.trim().toUpperCase(), value: pickupDate });
			});
		} else {
			Object.entries(PICKUP_DATE_TYPES_DB).forEach(([key, value]) => {
				pickupDateTypes.push({ dbVal: key, value: value });
			});
		}
		return pickupDateTypes;
	}

	get MINIMUM_TRUCK_COUNT(): number {
		let minimunTruckCount: number = 0,
			accountSettings: any = this.cargochief.getAccountSettings();
		if (IS_NOT_NULL_OR_UNDEFINED(accountSettings)) {
			minimunTruckCount = accountSettings.MINIMUM_TRUCK_COUNT;
		}
		return minimunTruckCount;
	}

	get MINIMUM_FREQUENCY(): number {
		let minimunFrequency: number = 0,
			accountSettings: any = this.cargochief.getAccountSettings();
		if (IS_NOT_NULL_OR_UNDEFINED(accountSettings)) {
			minimunFrequency = accountSettings.MINIMUM_FREQUENCY;
		}
		return minimunFrequency;
	}

	get DEFAULT_PICKUP_DATE_TYPE(): string {
		let defaultPickupDateType: string = 'Today',
			accountSettings: any = this.cargochief.getAccountSettings();
		if (IS_NOT_NULL_OR_UNDEFINED(accountSettings)) {
			defaultPickupDateType = accountSettings.DEFAULT_PICK_UP_DATE_TYPE;
		}
		return defaultPickupDateType;
	}

	get DEFAULT_CAPACITY_SEARCH_TAB(): string {
		let defaultCapacitySearchTab: string = '',
			accountSettings: any = this.cargochief.getAccountSettings();
		if (IS_NOT_NULL_OR_UNDEFINED(accountSettings)) {
			defaultCapacitySearchTab = accountSettings.DEFAULT_CAPACITY_SEARCH_TAB;
		}
		return defaultCapacitySearchTab;
	}

	get DECLARED_VALUE_REQUIRED(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.DECLARED_VALUE_REQUIRED);
	}

	get PICK_UP_APPOINTMENT_REQUIRED(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.PICK_UP_APPOINTMENT_REQUIRED);
	}

	get DELIVERY_APPOINTMENT_REQUIRED(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.DELIVERY_APPOINTMENT_REQUIRED);
	}

	get DEMO_ACCOUNT(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.DEMO_ACCOUNT);
	}

	get BCC_USER_OFFERS(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.BCC_USER_OFFERS);
	}

	get SEND_RATE_CON(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.SEND_RATE_CON);
	}

	get WEIGHT_REQUIRED(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.WEIGHT_REQUIRED);
	}

	get TRUCK_SEARCH_ENABLED(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.TRUCK_SEARCH_ENABLED);
	}

	get IS_FEEDBACK_ENABLED(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.IS_FEEDBACK_ENABLED);
	}

	get FREQUENCY_ENABLED(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.FREQUENCY_ENABLED);
	}

	get TARGET_MARGIN(): number {
		return this.parseNumericValues(ACCOUNT_SETTINGS.TARGET_MARGIN);
	}

	get PROJECT_PROBABILITY(): boolean {
		return (
			environment.PROJECT_FEATURES.PROBABILITY &&
			this.parseBooleanValue(ACCOUNT_SETTINGS.PROJECT_PROBABILITY)
		);
	}

	get PROJECT_PROBABILITY_SETTINGS(): ProjectProbability {
		const priorities: string = this.getSettingValue(ACCOUNT_SETTINGS.PROJECT_PROBABILITY_SETTINGS);
		if (!this.utilService.isNullOrUndefined(priorities) && IS_NOT_EMPTY(priorities)) {
			return JSON.parse(priorities);
		}
		return null;
	}

	get DEFAULT_TO_MY_PROJECTS(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.DEFAULT_TO_MY_PROJECTS);
	}

	get ANNUAL_VOLUME(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.ANNUAL_VOLUME);
	}

	get SCORECARDS(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.SCORECARDS);
	}

	get CUSTOMERS(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.CUSTOMERS);
	}

	get CARRIER_RATES(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.CARRIER_RATES);
	}

	get POST_LOAD(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.POST_LOAD);
	}

	get VOLUME_REQUIRED(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.VOLUME_REQUIRED);
	}

	get PROJECTS_BOARD(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.PROJECTS_BOARD);
	}

	get USER_ENABLED_PROJECT_BOARD(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.USER_ENABLED_PROJECT_BOARD);
	}

	get MCLEOD_INTEGRATION(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.MCLEOD_INTEGRATION);
	}

	get HAZMAT(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.HAZMAT);
	}

	get STRONG_LANES(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.STRONG_LANES);
	}

	get STRONG_LANES_SETTINGS(): string {
		const settingValue: string = this.getSettingValue(ACCOUNT_SETTINGS.STRONG_LANES_SETTINGS);
		if (!this.utilService.isNullOrUndefined(settingValue) && IS_NOT_EMPTY(settingValue)) {
			return JSON.parse(settingValue);
		}
		return null;
	}

	get TEAMS_CAPACITY(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.TEAMS_CAPACITY);
	}

	get BOOKING_ASSISTANT(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.BOOKING_ASSISTANT);
	}

	/**
	 * CN-8994: whether Cargo Chief allows this account to turn Booking Assistant Automation (DFM) on.
	 * An account without it can still run Booking Assistant manually from the client.
	 *
	 * Defaults to TRUE when the setting is absent, which is why this does not use parseBooleanValue().
	 * No account has a row for it, and it gates a capability every account already had — reading a
	 * missing setting as false would take the switch away from everyone the moment this deploys.
	 */
	get BOOKING_ASSISTANT_AUTOMATION_ALLOWED(): boolean {
		const value = this.getSettingValue(ACCOUNT_SETTINGS.BOOKING_ASSISTANT_AUTOMATION_ALLOWED);

		if (this.isValidSettingValue(value)) {
			try {
				return Boolean(JSON.parse(value));
			} catch (error) {
				// A malformed stored value is not evidence that Cargo Chief revoked anything, and it must
				// not throw out of a getter the Booking Assistant page reads on load. Fail open, as the
				// server gate does for an unrecognised value.
				console.warn('BOOKING_ASSISTANT_AUTOMATION_ALLOWED could not be parsed: ', value);
				return true;
			}
		}

		return true;
	}

	get CUSTOIZABLE_EMAILS(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.CUSTOMIZABLE_EMAILS);
	}

	get EMAIL_ROUTING(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.EMAIL_ROUTING);
	}

	get CONFIDENCE_SCORE(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.CONFIDENCE_SCORE);
	}

	get BOOKING_ASSISTANT_SETTINGS(): {
		rateRecencyLimit: string;
		pickupDateThreshold: string;
		partialLoadEnabled: boolean;
	} {
		return {
			rateRecencyLimit: this.getSettingValue(ACCOUNT_SETTINGS.RATE_RECENCY_LIMIT),
			pickupDateThreshold: this.getSettingValue(ACCOUNT_SETTINGS.PICKUP_DATE_THRESHOLD),
			partialLoadEnabled: this.parseBooleanValue(ACCOUNT_SETTINGS.PARTIAL_LOAD_ENABLED),
		};
	}

	get GET_OPTION_VISIBLE(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.GET_OPTION_VISIBLE);
	}

	get V2_AMAZON_FREIGHT(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.AMAZON_FREIGHT);
	}

	get ROLE_BASED_CUSTOMER_DATA_ACCESS(): boolean {
		const ROLE_BASED_CUSTOMER_DATA_ACCESS = this.parseBooleanValue(
			ACCOUNT_SETTINGS.ROLE_BASED_CUSTOMER_DATA_ACCESS
		);
		if (ROLE_BASED_CUSTOMER_DATA_ACCESS) {
			return this.utilService.isUserRole(USER_ROLES.CUSTOMER_REVIEWER);
		}
		return !ROLE_BASED_CUSTOMER_DATA_ACCESS;
	}

	get BA_PERFORMANCE_METRICS_ENABLED(): boolean {
		return (
			this.utilService.isUserRoleAdmin() &&
			this.parseBooleanValue(ACCOUNT_SETTINGS.BA_PERFORMANCE_METRICS_ENABLED)
		);
	}

	get CARRIER_QUALITY(): boolean {
		return environment.SHOW_QUALITY && this.parseBooleanValue(ACCOUNT_SETTINGS.CARRIER_QUALITY);
	}

	get ENABLE_OUT_OF_NETWORK_CARRIERS(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.ENABLE_OUT_OF_NETWORK_CARRIERS);
	}

	get GREENSCREENS_RATE_IN_RFP(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.GREENSCREENS_RATE_IN_RFP);
	}

	get SEND_LOADS_WITH_NO_RATE(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.SEND_LOADS_WITH_NO_RATE);
	}

	get SHOW_LOADS_PREDICTED_PRICE(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.SHOW_LOADS_PREDICTED_PRICE);
	}

	get INCLUDE_DISPATCHER_IN_BOOK_NOW_OFFER(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.INCLUDE_DISPATCHER_IN_BOOK_NOW_OFFER);
	}

	get HIDE_CUSTOMER_DETAILS_FROM_NON_ADMIN_USERS(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.HIDE_CUSTOMER_DETAILS_FROM_NON_ADMIN_USERS);
	}

	get NON_ADMIN_SEE_OWN_LOADS_ONLY(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.NON_ADMIN_SEE_OWN_LOADS_ONLY);
	}

	get LANE_ANALYTICS(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.LANE_ANALYTICS);
	}

	get LANE_CARRIER_ANALYTICS(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.LANE_CARRIER_ANALYTICS);
	}

	get WORKFLOW_PERFORMANCE_REPORT(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.WORKFLOW_PERFORMANCE_REPORT);
	}

	get PROCUREMENT_VERSIONS(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.PROCUREMENT_VERSIONS);
	}

	// CN-8861 — gates the in-app Bulk Rating experience: turns the Procurement
	// top-nav into a dropdown and surfaces the per-lane results view. When false
	// the account keeps today's read-only RFP status screen and plain nav link.
	get BULK_RATING_ENABLED(): boolean {
		// Fail closed: getSettingValue() returns true when the settings payload is not yet
		// available (getAccountSettings() null/undefined, e.g. pre-login). This opt-in flag
		// must default off in that state so the nav/rename are never exposed prematurely.
		if (this.utilService.isNullOrUndefined(this.cargochief.getAccountSettings())) {
			return false;
		}
		return this.parseBooleanValue(ACCOUNT_SETTINGS.BULK_RATING_ENABLED);
	}

	// CN-8861 — single source of truth for the feature's display name so the tab,
	// import button, and upload dialog stay in sync: "Bulk Rating" when the new
	// experience is on, otherwise the legacy "RFP".
	get bulkRatingFeatureName(): string {
		// CN-8875: the feature is named "Bulk Rating" for everyone now (display-only; the
		// backend key stays price_lanes). Previously this returned "RFP" for flag-off accounts,
		// which left the old page inconsistently labelled. The BULK_RATING_ENABLED flag still
		// gates the nav dropdown + in-app results view — just not the name.
		return 'Bulk Rating';
	}

	// CN-8712 — feature flag gating the dashboard layout preference toggle.
	get DASHBOARD_VERSIONS(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.DASHBOARD_VERSIONS);
	}

	get TOP_CARRIERS_ENABLED(): boolean {
		return this.parseBooleanValue(ACCOUNT_SETTINGS.TOP_CARRIERS_ENABLED);
	}

	parseNumericValues(settingName: string): number {
		const value = this.getSettingValue(settingName);
		if (this.isValidSettingValue(value)) {
			return Number(JSON.parse(value));
		}
		return null;
	}

	parseBooleanValue(settingName: string): boolean {
		const value = this.getSettingValue(settingName);
		if (this.isValidSettingValue(value)) {
			return Boolean(JSON.parse(value));
		}
		return false;
	}

	isValidSettingValue(settingValue: string): boolean {
		return (
			!this.utilService.isNullOrUndefined(settingValue) &&
			IS_NOT_EMPTY(settingValue) &&
			settingValue != 'null'
		);
	}

	getSettingValue(settingName: string): any {
		const accountSettings: any = this.cargochief.getAccountSettings();
		if (!this.utilService.isNullOrUndefined(accountSettings)) {
			return accountSettings[settingName];
		}
		return true;
	}

	updateCarrierWarmupSetting(value: boolean): void {
		const url = this.urlService.updateCarrierWarmupSetting();
		this.restService.POST(url, { value }).subscribe(
			(result) => {
				this.utilService.showSnackbar(result['data'], SNACKBAR.SUCCESS, SNACKBAR.ACTION);
				this.loginSetupService.updateLoginSetup();
			},
			(error) => {
				const message =
					error['data'] ||
					'Could not update carrier warmup settings for your account. Please contact CargoChief Support.';
				this.utilService.showSnackbar(message, SNACKBAR.ERROR, SNACKBAR.ACTION);
			}
		);
	}
}
